import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import "../assets/css/portfolio.css";
import portfolioItems from "../assets/data/portfolio.json";

const MAX_DESCRIPTION_CHARS = 130;

const truncate = (text, maxLength) => {
    if (typeof text !== "string" || text.length <= maxLength) {
        return text;
    }

    return `${text.slice(0, maxLength).trimEnd()}…`;
};

const Portfolio = () => {
    const { t } = useTranslation();
    const [selectedProject, setSelectedProject] = useState(null);

    const { items, settings } = portfolioItems;

    const closeOverlay = useCallback(() => {
        setSelectedProject(null);
    }, []);

    const openOverlay = (project) => {
        setSelectedProject(project);
    };

    useEffect(() => {
        if (!selectedProject) {
            return undefined;
        }

        const handleEscapeKey = (event) => {
            if (event.key === "Escape") {
                closeOverlay();
            }
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        window.addEventListener("keydown", handleEscapeKey);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleEscapeKey);
        };
    }, [closeOverlay, selectedProject]);

    const stopOverlayPropagation = (event) => {
        event.stopPropagation();
    };

    return (
        <>
            <section className="portfolio" id="portfolio">
                <div className="head">
                    <h1>{t("portfolio.title")}</h1>
                    <h4>{t("portfolio.subtitle")}</h4>
                    <hr />
                </div>

                <div className="portfolio-grid">
                    {items.map((project, index) => (
                        <button
                            key={index}
                            type="button"
                            className="portfolio-card"
                            onClick={() => openOverlay(project)}
                        >
                            <div className="portfolio-image-wrap">
                                <img
                                    src={project.banner.image}
                                    alt={t(project.titleKey)}
                                    draggable="false"
                                />
                                <div className="hover-img">
                                    <span
                                        className="hover-img-title"
                                        role={
                                            project.banner.url
                                                ? "link"
                                                : undefined
                                        }
                                        tabIndex={
                                            project.banner.url ? 0 : undefined
                                        }
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            if (project.banner.url) {
                                                window.open(
                                                    project.banner.url,
                                                    "_blank",
                                                    "noopener,noreferrer",
                                                );
                                            }
                                        }}
                                        onKeyDown={(event) => {
                                            if (
                                                project.banner.url &&
                                                (event.key === "Enter" ||
                                                    event.key === " ")
                                            ) {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                window.open(
                                                    project.banner.url,
                                                    "_blank",
                                                    "noopener,noreferrer",
                                                );
                                            }
                                        }}
                                    >
                                        {project.banner.title}
                                    </span>
                                    {project.banner.url && (
                                        <span className="hover-img-url">
                                            {project.banner.url}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="portfolio-content">
                                <h3>{t(project.titleKey)}</h3>
                                <p>{truncate(t(project.descriptionKey), MAX_DESCRIPTION_CHARS)}</p>
                            </div>
                        </button>
                    ))}
                </div>
            </section>

            {selectedProject && (
                <div className="portfolio-overlay" onClick={closeOverlay}>
                    <div
                        className="portfolio-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="portfolio-modal-title"
                        onClick={stopOverlayPropagation}
                    >
                        <div className="portfolio-modal-header">
                            <h3 id="portfolio-modal-title">
                                {t(selectedProject.titleKey)}
                            </h3>
                            <button
                                type="button"
                                className="portfolio-close"
                                onClick={closeOverlay}
                                aria-label={t("portfolio.close")}
                            >
                                ×
                            </button>
                        </div>

                        <Swiper
                            className="portfolio-slider"
                            modules={[Autoplay, Pagination, Navigation]}
                            loop={settings.loop ?? true}
                            autoplay={
                                settings.autoplay ?
                                    {
                                        delay: settings.autoplaySpeed ?? 5000,
                                        disableOnInteraction: false,
                                    }
                                :   false
                            }
                            pagination={{
                                clickable: true,
                            }}
                            navigation={true}
                            speed={settings.speed ?? 650}
                            slidesPerView={1}
                        >
                            {(
                                selectedProject.images?.length ?
                                    selectedProject.images
                                :   [selectedProject.image]
                            ).map((image, index) => (
                                <SwiperSlide key={index}>
                                    <div className="portfolio-slide-content">
                                        <img
                                            src={image.src}
                                            alt={t("portfolio.slideAlt", {
                                                index: index + 1,
                                            })}
                                            draggable="false"
                                        />
                                        <p className="portfolio-slide-description">
                                            {t(image.descriptionKey)}
                                        </p>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>

                        <div className="portfolio-body">
                            <p>{t(selectedProject.descriptionKey)}</p>
                            <div className="portfolio-tech">
                                {selectedProject.technologies.map(
                                    (tech, idx) => (
                                        <span key={idx}>{tech}</span>
                                    ),
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Portfolio;
